import { memo, useCallback } from 'react';

import { stringifyError } from 'shared/functions';
import { WithError } from '@eva/types/dto';

import type { ControlDescriptor, ControlState, RowState } from '../types';

export interface ControlProps<E, G> {
  entity: E;
  groups: G[];
  rowState: RowState;
  state: ControlState;
  control: ControlDescriptor<E, G>;
  getEntityId: (entity: E) => number;
  updateState: (controlId: string, changes: Partial<ControlState>) => void;
  updateRowState: (changes: Partial<RowState>) => void;
}

const Control = <E, G extends WithError>({
  entity,
  groups,
  control,
  state,
  rowState,
  getEntityId,
  updateState,
  updateRowState,
}: ControlProps<E, G>) => {
  const updateThisState = useCallback((changes) => updateState(control.actionId, changes), [control, updateState]);

  const action = useCallback(
    (e: E, ...rest) => {
      updateThisState({ inProgress: true });

      return control
        .action(e, ...rest)
        .then((response) => {
          updateThisState({ inProgress: false });
          updateRowState({
            inlineForm: null,
            error: null,
          });
          return Promise.resolve(response);
        })
        .catch((error) => {
          updateThisState({
            inProgress: false,
          });
          if (rowState.inlineForm) {
            updateRowState({
              error: stringifyError(error),
            });
          } else {
            return Promise.reject(error);
          }
        });
    },
    [control, rowState, updateThisState, updateRowState],
  );

  return control.render({
    entity,
    groups,
    action,
    state,
    rowState,
    getEntityId,
    updateRowState,
    updateState: updateThisState,
  });
};

// eslint-disable-next-line import/no-default-export
export default memo(Control);
